<template>
  <div
    class="product-card"
    :class="{
      [$class('loading')]: loading ,
      'product-card--inline': inline,
      'show-secondary-image': showSecondaryImage,
    }"
  >
  <div v-if="currentCountry == 'US' || currentCountry == 'CA'">
    <product-badge
      v-if="!inline && badge"
      :text="badge"
      :badge-class="productBadgeClass"
    />
  </div>
    <div
      v-if="inline"
      class="product-card__image-container"
    >
      <div class="product-card__primary-image">
        <a :href="formatUrl(product.url)">
          <responsive-image
            v-if="product.featuredImage"
            :image="product.featuredImage"
            min-max="200-500"
            ratio="4:3"
          />

          <responsive-image
            v-else-if="product.featuredMedia"
            :image="product.featuredMedia.image"
            min-max="200-500"
            ratio="4:3"
          />
        </a>
      </div>
    </div>

    <div
      v-else
      class="product-card__image-container"
    >
      <div
        v-if="product.featuredImage"
        class="product-card__primary-image"
      >
        <a
          :href="formatUrl(product.url)"
        >
          <responsive-image
            v-if="!isMobile"
            :image="product.featuredImage"
            min-max="600-1000"
            ratio="4:3"
          />
        </a>

        <splide
          v-if="product.images && isMobile"
          :has-track="false"
          :options="config"
        >
          <splide-track>
            <splide-slide
              v-for="(image, index) of product.images"
              :key="`image-${index}`"
            >
              <a
                :href="formatUrl(product.url)"
              >
                <responsive-image
                  :image="image"
                  min-max="600-1000"
                  ratio="4:3"
                  @image-loaded="showSecondaryImage = true"
                />
              </a>
            </splide-slide>
          </splide-track>

          <div class="splide__arrows">
            <button
              type="button"
              class="splide__arrow splide__arrow--prev"
            >
              <chevron-left />
            </button>

            <button
              type="button"
              class="splide__arrow splide__arrow--next"
            >
              <chevron-right />
            </button>
          </div>
        </splide>
      </div>

      <div
        v-if="product.secondaryImage && !isMobile || product.images.length >= 2 && !isMobile"
        class="product-card__secondary-image"
      >
        <a
          :href="formatUrl(product.url)"
        >
          <responsive-image
            v-if="product.images.length == 2"
            :image="product.secondaryImage"
            min-max="200-500"
            ratio="4:3"
            @image-loaded="showSecondaryImage = true"
          />
        </a>

        <splide
          v-if="product.images.length > 2 && !isMobile"
          :has-track="false"
          :options="config"
        >
          <splide-track>
            <splide-slide
              v-for="(image, index) of product.images.slice(1)"
              :key="`image-${index}`"
            >
              <a
                :href="formatUrl(product.url)"
              >
                <responsive-image
                  :image="image"
                  min-max="200-500"
                  ratio="4:3"
                  @image-loaded="showSecondaryImage = true"
                />
              </a>
            </splide-slide>
          </splide-track>

          <div class="splide__arrows">
            <button
              type="button"
              class="splide__arrow splide__arrow--prev"
            >
              <chevron-left />
            </button>

            <button
              type="button"
              class="splide__arrow splide__arrow--next"
            >
              <chevron-right />
            </button>
          </div>
        </splide>
      </div>
    </div>

    <a
      :href="formatUrl(product.url)"
      class="product-card__footer"
    >
      <div
        v-if="product.compatibility"
        class="
          product-card__compatibility
          text-other-label-l
        "
        v-html="sanitizeCompatibility(product.compatibility)"
      />

      <p
        class="
        product-card__title
        text-heading-s
        text-heading-l-desktop
      "
        v-text="product.title"
      />

      <product-prices
        class="product-card__prices"
        :available="product?.available"
        :compare-at-price="product?.compareAtPrice?.amount"
        :price="product?.price?.amount"
        :start-price="product?.price?.startPrice"
        :varies="product.price.startPrice &&
          product.price.startPrice !== product?.price?.amount
        "
      />

      <product-rating :sku="product.sku" />
    </a>

    <div
      v-if="!inline"
      class="product-card__action"
    >
      <btn
        v-if="product.hasOnlyDefaultVariant && isTransactional"
        class="product-card__add-to-cart"
        :label="$string('product.cta.add_to_cart')"
        modifiers="fill block center"
        hover-only
        type="button"
        @click.prevent="handleAddToCart"
      >
        <template #hoverIcon>
          <icon-cart />
        </template>
      </btn>

      <btn
        v-else
        class="product-card__add-to-cart"
        :label="$string('product.cta.view_product')"
        modifiers="fill block center"
        hover-only
        type="button"
        @click.prevent="navigateToPDP"
      >
        <template #hoverIcon>
          <icon-arrow-right />
        </template>
      </btn>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} inline - Use inline layout.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import ProductPrices from '~global/product-prices/product-prices'
import ProductRating from '~async/product-rating/product-rating'
import ProductBadge from '~async/product-badge/product-badge.vue'
import ResponsiveImage from '~global/images/responsive-image'
import Btn from '~global/btn/btn'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import IconCart from '~icons/button/cart.svg'
import ChevronLeft from '~icons/directional-navigation/chevron/left.svg'
import ChevronRight from '~icons/directional-navigation/chevron/right.svg'

export default {
  name: 'ProductCard',

  components: {
    ProductPrices,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    Btn,
    IconArrowRight,
    IconCart,
    ChevronLeft,
    ChevronRight,
    ProductBadge,
    ProductRating,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Object,
      default: () => ({
        new: '',
        sale: '',
        most_popular: '',
        best_seller: '',
        freeShipping: '',
      }),
    },
    loading: {
      type: [Boolean],
      default: false,
      required: true,
    },
  },

  emits: {
    'display-options': () => false,
    'product-card-loaded': () => false,
  },

  data() {
    return {
      config: {
        arrows: true,
        pagination: false,
      },
      isTransactional: false,
      loadSecondaryImage: false,
      showSecondaryImage: false,
      currentCountry: `${cnvs.store.country}`,
      badge: false,
      sale: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    productBadgeClass() {
      if (!this.badge) {
        return
      }

      const badge = this.badge.split(' ')
      const badgeClass = this.sale ? this.$string('collection.badge.sale').toLowerCase() : badge[0].replace('_', '-').replace(' ', '-').toLowerCase()
      return `product-badge--${badgeClass}`
    },

  },

  watch: {

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      this.getTruncateLimit(this.isMobile)
    },
  },
  emits: {
    'product-card-loaded': () => true,
  },
  mounted() {
    if (window.cnvs.transactionalMarkets.includes(window.cnvs.store.country.toLowerCase())) {
      this.isTransactional = true
    }

    this.getProductBadge()
    this.emitState()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),
    emitState() {
      this.$emit('product-card-loaded')
    },
    sanitizeCompatibility(values) {
      let output = ``
      let truncated = false
      const products = values.split('|')
      const limit = this.getTruncateLimit(this.isMobile)

      products.forEach((product, index) => {

        if (index < limit) {
          output += `<span class="product-card__compatibility-product">${product}</span>`
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!truncated) {
            output += `<span class="product-card__compatibility-product">+${products.length - index}</span>`
            truncated = true
          }
        }
      })

      return output
    },

    getTruncateLimit(isMobile) {
      if (isMobile) {
        return 3
      }
      return 4
    },

    async handleAddToCart() {
      const variantId = this.product.variants ? this.product.variants[0].id : this.product.id

      const items = [
        {
          id: variantId,
          quantity: 1,
        },
      ]

      try {
        await this.addItem(items)

        if (
          cnvs.settings.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

      } catch (error) {
        cnvs.ReportError('Failed to add product to cart', error)
      }
    },
    getProductBadge() {
      const { product } = this
      const sale = product.price.amount < product.compareAtPrice.amount
      const tags = product.tags ? product.tags.find((tag) => tag.includes('badge:')) : false
      const badges = product.badge ? product.badge[0] : false
      const badge = tags || badges
      this.sale = sale

      if (!sale && !badge) {
        return
      }

      this.badge = sale ? this.$string('collection.badge.sale') : this.labels[`${badge}`]
    },

    navigateToPDP() {
      const { page } = window.cnvs
      const klevuPage = page.type === 'collection' || page.type === 'search'

      if (klevuPage && this.isTransactional) {
        this.$emit('display-options')
        return
      }

      window.location = this.formatUrl(this.product.url)
    },

    /**
   * Format url to include market handle url.
   * @param {String} url - Url string.
   * @returns {String}
   */
    formatUrl(url) {
      const { origin } = window.cnvs.request
      const fullUrl = url.includes(origin) ? url : origin + url

      const pathname = new URL(fullUrl).pathname
      const primary = cnvs.strings.defaultLocalization.primaryMarket
      const country = cnvs.store.country.toLowerCase()
      const language = cnvs.store.language.toLowerCase()
      const isPrimaryDomain = primary.domain.toLowerCase() === country
      const isPrimaryLocale = primary.locale.toLowerCase() === language
      const isUSPath = country === 'us' && url.includes(`/${language}/`)
      let updatedPathname = pathname

      if (url.includes(`${language}-${country}`) || isUSPath) {
        return fullUrl
      }

      if (isPrimaryDomain) {
        updatedPathname = updatedPathname.replace(`-${country}`, '')

        if (isPrimaryLocale) {
          updatedPathname = updatedPathname.replace(`/${language}`, '')
        }
      }

      return updatedPathname
    },
   
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
